import React, { useState } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Edit, Plus } from 'lucide-react'
import SofaBlueprint from '@/configurator/SofaBlueprint'
import { CHECKERBOARD_STYLE } from './GeneratedBlueprintPanel'
import SofaFormEditForm from './SofaFormEditForm'
import SofaFormCreateForm from './SofaFormCreateForm'
import useOpenDrawer from '@/hooks/useOpenDrawer'
import { DataTable } from '@/components/shared/data-table/data-table'
import { DataTableToolbar } from '@/components/shared/data-table/data-table-toolbar'
import { ColumnDef } from '@tanstack/react-table'
import { gql, useQuery } from '@apollo/client'
import { SofaFormTypeGridSelect } from '@/components/shared/inputComponents/SofaFormTypeGridSelect'

export const GET_SOFA_FORMS = gql`
  query GetSofaForms($productId: Int!) {
    findUniqueProductContainer(where: { id: $productId }) {
      advanced_product {
        sofa_forms {
          id
          code
          enabled
          left_and_right
          name
          type
          blueprint_thumbnail_url
          dimensions {
            height
            width
            length
            armrest_width
            backrest_width
            mattress_width
            mattress_length
            corner_part_length
            angle
            seat_height
            seat_depth
            seat_width
            extendable_part_length
            number_of_big_pillows
            number_of_small_pillows
            spread_of_big_pillows
            spread_of_small_pillows
            size_of_big_pillow
            size_of_pillow
            composition
            corner_radius
            seat_sections
            backrest_sections
            extension_type
            backrest_type
            covered_side
          }
          package_dimensions {
            id
            volume
            name
          }
        }
        _count {
          sofa_forms
        }
      }
    }
  }
`

interface PackageDimension {
  id: string
  volume: number
  name: string
}

interface SofaForm {
  id: string
  code: string
  enabled: boolean
  left_and_right: boolean
  name: string
  type: string
  /** Stored, pre-rendered PNG (1 px/cm) -- see GeneratedBlueprintPanel.tsx for the full contract. */
  blueprint_thumbnail_url?: string | null
  dimensions: {
    width?: number | null
    length?: number | null
    armrest_width?: number | null
    backrest_width?: number | null
    mattress_width?: number | null
    mattress_length?: number | null
    corner_part_length?: number | null
    angle?: number | null
    seat_height?: number | null
    extendable_part_length?: number | null
    number_of_big_pillows?: number | null
    number_of_small_pillows?: number | null
    spread_of_big_pillows?: number | null
    spread_of_small_pillows?: number | null
    size_of_big_pillow?: number | null
    size_of_pillow?: number | null
  }
  package_dimensions: PackageDimension[]
}

interface SofaFormsTableProps {
  productId: number
  initialCount: number
  advancedProductId: number
}

/**
 * Blueprint cell for a single Modules-table row.
 *
 * Prefers the stored, pre-rendered thumbnail PNG (zero extra DB queries --
 * `blueprint_thumbnail_url` rides the same DataLoader as the rest of this
 * query) and falls back to the live Konva `<SofaBlueprint>` render whenever
 * there is no stored thumbnail yet, or its URL 404s/errors (these are public
 * GCS URLs and can go stale). Hooks need a real component, not the inline
 * `cell` closure the column previously used.
 *
 * The failure is tracked by URL, not by a boolean: this table paginates
 * (`defaultPageSize={10}` in DataTable below), which reuses row/cell
 * instances by index across pages. A plain boolean would leak a failure
 * from row N on one page onto an unrelated row N on the next page; keying
 * off the URL self-corrects because the comparison is against the
 * newly-rendered row's own thumbnail URL.
 */
export function SofaFormBlueprintCell({ row }: { row: SofaForm }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const thumbnailUrl = row.blueprint_thumbnail_url
  const showImage = !!thumbnailUrl && failedUrl !== thumbnailUrl

  if (showImage) {
    return (
      <div
        className="flex h-[60px] w-[60px] items-center justify-center overflow-hidden rounded"
        style={CHECKERBOARD_STYLE}
      >
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <img
          src={thumbnailUrl!}
          alt={`${row.name} blueprint thumbnail`}
          loading="lazy"
          className="max-h-full max-w-full object-contain"
          onError={() => setFailedUrl(thumbnailUrl!)}
        />
      </div>
    )
  }

  return (
    <SofaBlueprint
      id={parseInt(row.id)}
      dimensions={row.dimensions}
      type={row.type}
      containerHeight="60px"
      containerWidth="60px"
    />
  )
}

export function SofaFormsTable({
  productId,
  initialCount,
  advancedProductId,
}: SofaFormsTableProps) {
  const openSofaFormEdit = useOpenDrawer(SofaFormEditForm)
  const openSofaFormCreate = useOpenDrawer(SofaFormCreateForm)
  const [showShapeSelector, setShowShapeSelector] = useState(false)

  const { loading, error, data } = useQuery(GET_SOFA_FORMS, {
    variables: { productId },
    skip: initialCount === 0,
  })

  const sofaForms =
    data?.findUniqueProductContainer?.advanced_product?.sofa_forms || []

  const handleShapeSelected = (typeName: string) => {
    setShowShapeSelector(false)
    openSofaFormCreate({ type: typeName, advancedProductId })
  }

  const columns: ColumnDef<SofaForm>[] = [
    {
      accessorKey: 'blueprint',
      header: 'Blueprint',
      cell: ({ row }) => <SofaFormBlueprintCell row={row.original} />,
      enableSorting: false,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => row.original.name,
      filterFn: (row, id, value) => {
        const searchValue = value.toLowerCase()
        const name = row.getValue(id) as string
        const code = row.original.code
        return (
          name.toLowerCase().includes(searchValue) ||
          code.toLowerCase().includes(searchValue)
        )
      },
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => (
        <div
          className={`space-x-2 ${!row.original.enabled ? 'opacity-30' : ''}`}
        >
          <Badge variant="outline">{row.original.type}</Badge>
          {row.original.left_and_right && (
            <Badge variant="outline">Left & Right</Badge>
          )}
          <span className="text-muted-foreground">{row.original.code}</span>
        </div>
      ),
    },
    {
      accessorKey: 'package_dimensions',
      header: 'Volume',
      cell: ({ row }) => {
        const totalVolume = row.original.package_dimensions.reduce(
          (sum, dim) => sum + (dim.volume || 0),
          0
        )
        return <div>{totalVolume.toFixed(2)} m³</div>
      },
      enableSorting: false,
      enableColumnFilter: false,
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              console.log('Edit Sofa Form', row.original)
              openSofaFormEdit(row.original)
            }}
          >
            <Edit />
          </Button>
        </div>
      ),
      enableSorting: false,
      enableColumnFilter: false,
    },
  ]

  const cardHeader = (
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle>Modules</CardTitle>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowShapeSelector(true)}
      >
        <Plus className="h-4 w-4 mr-1" />
        Add Module
      </Button>
    </CardHeader>
  )

  if (loading) {
    return (
      <Card>
        {cardHeader}
        <CardContent>
          <div className="h-24 flex items-center justify-center">
            Loading...
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        {cardHeader}
        <CardContent>
          <div className="text-red-500">Error loading sofa forms</div>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        {cardHeader}
        <CardContent>
          <DataTable
            columns={columns}
            data={sofaForms}
            defaultPageSize={10}
            toolbar={table => (
              <DataTableToolbar
                table={table}
                config={{
                  search: {
                    placeholder: 'Search modules by name or code...',
                    accessor: 'name',
                  },
                  columns: [],
                }}
              />
            )}
          />
        </CardContent>
      </Card>

      <SofaFormTypeGridSelect
        open={showShapeSelector}
        onOpenChange={setShowShapeSelector}
        onSelect={handleShapeSelected}
      />
    </>
  )
}
